import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  listInboundByStaffRecipient,
  listInboundByOrganization,
  listSentByStaff,
  listOutboundNoreplyByOrganization,
  getInboundDetail,
  getOutboundDetailForStaff,
  getOutboundDetailForOrgAdmin,
  getSentDetail,
  markInboundRead,
  sendOutboundTransactional,
} from "../../../services/emailCorrespondenceApi";
import { useUserData } from "../../../hooks/useUserData";
import { resolveEmailPlatformOrganizationGuid } from "../../../utils/emailPlatformOrganization";
import { getAvatarColor, getInitials } from "../../../utils/avatarUtils";
import {
  getSidebarModuleBadgeStyle,
  getSidebarModuleDotColor,
  resolveSidebarModuleKey,
} from "../../../utils/sidebarModuleTheme";
import {
  buildInboxRows,
  canViewOrgInboundReplies,
  canViewSystemSent,
  mergeMailboxRows,
  mergeStaffAndOrgInboundRows,
  rowIsInSentList,
  rowIsSentMailboxRow,
  resolveSentRecipientDisplay,
} from "../../../utils/notificationInboxUtils";
import {
  organizationHasCapaModule,
  organizationHasIsfModule,
  organizationHasParticipantManagerModule,
  organizationHasQmsModule,
  organizationHasStudyUserManagementModule,
  organizationHasEPlannerModule,
} from "../../../utils/orgModuleAccess";
import {
  getAllPermissionKeys,
  getSessionRoleName,
  hasPermission,
  isSystemAdministratorRole,
  parsePermissions,
} from "../../../utils/permissionUtils";
import { isSponsorCroSession } from "../../../utils/sponsorCroAccess";
import TextEditor from "../../common/TextEditor/TextEditor";
import "./NotificationCenterInbox.scss";

const STAR_STORAGE_KEY = "aq-nc-inbox-stars";

function apiOk(res) {
  return res && (res.success === 1 || res.success === "1");
}

function readIsReadFlag(row) {
  if (!row) return false;
  return !!(row.IsRead ?? row.isRead);
}

function formatCell(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function formatBodySnippet(bodyFields) {
  const text =
    bodyFields?.TextBody ||
    bodyFields?.textBody ||
    bodyFields?.HtmlBody ||
    bodyFields?.htmlBody ||
    "";
  const max = 8000;
  const s = String(text);
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

function htmlToPlainText(html) {
  if (!html || typeof html !== "string") return "";
  try {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || "").trim();
  } catch {
    return String(html).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }
}

function rowId(row) {
  return String(row?.Id || row?.id || "").trim();
}

function rowMailboxKind(row) {
  return String(row?.MailboxRowKind || row?.mailboxRowKind || "Inbound");
}

function rowModule(row) {
  return String(row?.Module || row?.module || "General").trim() || "General";
}

/**
 * Inbox module chips — scoped to organisation licensed modules (`userData.Modules`).
 * Admin is always available (platform / cross-cutting mail).
 */
const INBOX_MODULE_FILTER_BUCKETS = [
  {
    id: "admin",
    label: "Admin",
    alwaysVisible: true,
  },
  {
    id: "study",
    label: "Study & User Management",
    orgHasModule: organizationHasStudyUserManagementModule,
  },
  {
    id: "eisf",
    label: "eISF",
    orgHasModule: organizationHasIsfModule,
  },
  {
    id: "capa",
    label: "CAPA",
    orgHasModule: organizationHasCapaModule,
  },
  {
    id: "eplanner",
    label: "E-Planner",
    orgHasModule: organizationHasEPlannerModule,
  },
  {
    id: "participantManager",
    label: "Participant Manager",
    orgHasModule: organizationHasParticipantManagerModule,
  },
  {
    id: "qms",
    label: "QMS",
    orgHasModule: organizationHasQmsModule,
  },
];

function inboxModuleFilterBucketMeta(id) {
  return INBOX_MODULE_FILTER_BUCKETS.find((b) => b.id === id);
}

/** Maps raw API `Module` (e.g. `capa_action_planning`) → sidebar bucket id, or "" for general/other. */
function inboxModuleFilterIdForRow(row) {
  const k = resolveSidebarModuleKey(rowModule(row));
  if (!k || k === "notifications") return "";
  return k;
}

function rowMatchesInboxModuleFilter(row, filterId) {
  if (!filterId) return true;
  return inboxModuleFilterIdForRow(row) === filterId;
}

/** Org-licensed modules only; Admin always shown. */
function visibleInboxModuleBuckets(userData) {
  return INBOX_MODULE_FILTER_BUCKETS.filter((b) => {
    if (b.alwaysVisible) return true;
    if (typeof b.orgHasModule === "function") return b.orgHasModule(userData);
    return false;
  });
}

function recordIdFromRow(row) {
  if (!row) return "";
  return String(row.RecordId ?? row.recordId ?? "").trim();
}

function rowSubject(row) {
  return String(row?.Subject || row?.subject || "").trim() || "(No subject)";
}

function rowFrom(row) {
  return String(row?.From || row?.from || "").trim() || "—";
}

function rowPreview(row) {
  return String(
    row?.TextBodyPreview ||
      row?.textBodyPreview ||
      row?.HtmlBodyPreview ||
      row?.htmlBodyPreview ||
      "",
  ).trim();
}

function rowReceived(row) {
  return row?.ReceivedUtc || row?.receivedUtc || null;
}

/** Prefer API `ReceivedAtDisplay` (dd/MM/yyyy from SQL); fallback if older backend. */
function rowReceivedDisplay(row) {
  if (!row) return "—";
  const pre = row.ReceivedAtDisplay ?? row.receivedAtDisplay;
  if (pre != null && String(pre).trim() !== "") return String(pre).trim();
  return formatMailboxDateTime(rowReceived(row));
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

/** 12h clock from explicit calendar parts (day + month are never swapped). */
function formatTwelveFromParts(day, month, year, h24, minutes) {
  const tt = h24 >= 12 ? "PM" : "AM";
  let h12 = h24 % 12;
  if (h12 === 0) h12 = 12;
  return `${pad2(day)}/${pad2(month)}/${year} ${pad2(h12)}:${pad2(minutes)} ${tt}`;
}

function formatTwelveFromUtcDate(d) {
  if (Number.isNaN(d.getTime())) return "—";
  return formatTwelveFromParts(
    d.getUTCDate(),
    d.getUTCMonth() + 1,
    d.getUTCFullYear(),
    d.getUTCHours(),
    d.getUTCMinutes(),
  );
}

/**
 * List + reading pane: `dd/mm/yyyy hh:mm AM/PM` with **day first**, **month second**.
 * Parses `yyyy-mm-ddTHH:mm:ss` from SQL / JSON as **UTC wall** when no `Z` / offset
 * (avoids JS treating `2026-05-11T19:00` as local and swapping calendar day).
 */
function formatMailboxDateTime(iso) {
  const s = String(iso ?? "").trim();
  if (!s) return "—";
  if (s.startsWith("/Date(")) {
    const m = s.match(/\/Date\((-?\d+)\)\//);
    if (m) return formatTwelveFromUtcDate(new Date(Number(m[1])));
  }

  const m = s.match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{1,2}):(\d{2})(?::(\d{2}))?(\.\d+)?\s*(Z|[+-]\d{2}:\d{2}(?::\d{2})?)?\s*$/i,
  );
  if (m) {
    const year = parseInt(m[1], 10);
    const month = parseInt(m[2], 10);
    const day = parseInt(m[3], 10);
    const h24 = parseInt(m[4], 10);
    const minutes = parseInt(m[5], 10);
    const hasTz = !!(m[8] && String(m[8]).length);
    if (hasTz) {
      return formatTwelveFromUtcDate(new Date(s));
    }
    return formatTwelveFromParts(day, month, year, h24, minutes);
  }

  return formatTwelveFromUtcDate(new Date(s));
}

/** Milliseconds for grouping — same UTC rules as {@link formatMailboxDateTime}. */
function parseMailboxUtcMs(iso) {
  const s = String(iso ?? "").trim();
  if (!s) return NaN;
  if (s.startsWith("/Date(")) {
    const m = s.match(/\/Date\((-?\d+)\)\//);
    if (m) return Number(m[1]);
  }
  const m = s.match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{1,2}):(\d{2})(?::(\d{2}))?(\.\d+)?\s*(Z|[+-]\d{2}:\d{2}(?::\d{2})?)?\s*$/i,
  );
  if (m) {
    const year = parseInt(m[1], 10);
    const month = parseInt(m[2], 10);
    const day = parseInt(m[3], 10);
    const h24 = parseInt(m[4], 10);
    const minutes = parseInt(m[5], 10);
    const sec = m[6] != null ? parseInt(m[6], 10) : 0;
    const ms = m[7] ? Math.min(999, Math.round(parseFloat(m[7]) * 1000)) : 0;
    const hasTz = !!(m[8] && String(m[8]).length);
    if (hasTz) {
      const d = new Date(s);
      return Number.isNaN(d.getTime()) ? NaN : d.getTime();
    }
    return Date.UTC(year, month - 1, day, h24, minutes, sec, ms);
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? NaN : d.getTime();
}

function shortFrom(from) {
  const s = String(from || "").trim();
  if (!s) return "—";
  const m = s.match(/<([^>]+)>/);
  if (m) return m[1].trim();
  return s.length > 48 ? `${s.slice(0, 45)}…` : s;
}

/** Gmail-style sender label: prefer display name over bare email. */
function senderDisplayName(from) {
  const s = String(from || "").trim();
  if (!s) return "Unknown";
  const named = s.match(/^(.+?)\s*<([^>]+)>$/);
  if (named) {
    const name = named[1].replace(/^["']|["']$/g, "").trim();
    if (name) return name.length > 26 ? `${name.slice(0, 23)}…` : name;
    const email = named[2].trim();
    return email.includes("@") ? email.split("@")[0] : email;
  }
  if (s.includes("@")) return s.split("@")[0];
  return s.length > 26 ? `${s.slice(0, 23)}…` : s;
}

/** First email in `Name <a@b>` or bare address; empty if none. */
function parseEmailFromHeader(fromHeader) {
  const s = String(fromHeader || "").trim();
  if (!s) return "";
  const angle = s.match(/<([^>]+)>/);
  if (angle) {
    const inner = angle[1].trim();
    const em = inner.match(/[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/);
    return em ? em[0].trim() : inner;
  }
  const em = s.match(/[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/);
  return em ? em[0].trim() : "";
}

function withRePrefix(subject) {
  const t = String(subject || "").trim();
  if (/^re:\s*/i.test(t)) return t;
  return `Re: ${t || "(No subject)"}`;
}

const EMPTY_QUILL_HTML = "<p><br></p>";

function dataTransferHasFiles(dt) {
  if (!dt || !dt.types) return false;
  try {
    if (typeof dt.types.contains === "function") return dt.types.contains("Files");
  } catch {
    /* ignore */
  }
  return Array.from(dt.types).includes("Files");
}

/** True when Quill body has no visible text (ignores empty paragraphs). */
function isQuillBodyEmpty(html) {
  return htmlToPlainText(html || "").trim().length === 0;
}

function readFileAsAttachmentPart(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result || "");
      const i = s.indexOf(",");
      const b64 = i >= 0 ? s.slice(i + 1) : s;
      resolve({
        FileName: file.name || "attachment",
        ContentType: file.type && file.type.trim() ? file.type.trim() : "application/octet-stream",
        ContentInBase64: b64,
      });
    };
    reader.onerror = () => reject(new Error("Could not read file."));
    reader.readAsDataURL(file);
  });
}

/** Escape for RegExp source (bracketed org prefix in subject). */
function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Remove `[OrganizationDisplayName] ` prefix from outbound subjects (see EmailCorrespondenceSetUp.PrefixOutboundSubjectWithDisplayName). */
function stripBracketedOrgSubject(subject, orgDisplayName) {
  const sub = String(subject || "").trim();
  const org = String(orgDisplayName || "").trim();
  if (!sub || !org) return sub;
  const re = new RegExp("^\\[" + escapeRegExp(org) + "\\]\\s*", "i");
  const next = sub.replace(re, "").trim();
  return next || sub;
}

/** Remove leading org display line + blank lines from plain body (PrefixOutboundPlainBodyWithDisplayName). */
function stripLeadingOrgFromPlainText(text, orgDisplayName) {
  const org = String(orgDisplayName || "").trim();
  let t = String(text || "");
  if (!org || !t) return t;
  const re = new RegExp("^\\s*" + escapeRegExp(org) + "\\s*(?:\\r?\\n){2,}", "i");
  const next = t.replace(re, "");
  return next === t ? t : next.trimStart();
}

/** Remove first branding `<div><strong>Org</strong></div>` from HTML body when it matches session org. */
function stripLeadingOrgFromHtml(html, orgDisplayName) {
  const org = String(orgDisplayName || "").trim();
  if (!org || !html || typeof html !== "string") return html;
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const first = doc.body.firstElementChild;
    if (!first || first.tagName !== "DIV") return html;
    const strong = first.querySelector(":scope > strong");
    if (!strong) return html;
    const txt = (strong.textContent || "").trim();
    if (!txt || txt.toLowerCase() !== org.toLowerCase()) return html;
    first.remove();
    return doc.body.innerHTML.replace(/^\s+/, "");
  } catch {
    return html;
  }
}

function sessionOrganizationDisplayName(userData) {
  if (!userData) return "";
  return String(
    userData.OrganizationName ||
      userData.organizationName ||
      userData.OrgName ||
      userData.orgName ||
      "",
  ).trim();
}

function rowModuleBadgeLabel(row, max = 20) {
  const bid = inboxModuleFilterIdForRow(row);
  if (bid) {
    const meta = inboxModuleFilterBucketMeta(bid);
    if (meta?.label) {
      const lab = meta.label;
      return lab.length > max ? `${lab.slice(0, max - 1)}…` : lab;
    }
  }
  const mod = rowModule(row);
  if (!mod || mod.toLowerCase() === "general") return "General";
  // Prefer product label for Sponsor/CRO study-access mail (legacy Module = SponsorCro).
  const modKey = mod.replace(/[\s/_-]+/g, "").toLowerCase();
  if (modKey === "sponsorcro") {
    const lab = "Sponsor/CRO";
    return lab.length > max ? `${lab.slice(0, max - 1)}…` : lab;
  }
  const pretty = mod.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return pretty.length > max ? `${pretty.slice(0, max - 1)}…` : pretty;
}

function previewLooksLikeHtml(s) {
  if (!s || typeof s !== "string") return false;
  const t = s.trim();
  return /<[a-z][\s\S]*>/i.test(t);
}

/** Strip risky tags/attrs before rendering HTML from mail/API (not a full sanitizer). */
function toReactQmsLibraryUrl(href) {
  const raw = String(href || "").trim();
  if (!raw) return null;

  if (/\/qms\/library\?documentId=/i.test(raw)) {
    if (/^https?:\/\//i.test(raw)) return raw;
    const path = raw.startsWith("/") ? raw : `/${raw}`;
    return `${window.location.origin}${path}`;
  }

  if (/Home\/Index/i.test(raw) || /DMS_DOCUMENTLIBRARY/i.test(raw)) {
    const match = raw.match(/[?&]did=([^&"'<>]+)/i);
    if (match?.[1]) {
      let docId = match[1];
      try {
        docId = decodeURIComponent(docId);
      } catch {
        /* keep raw */
      }
      return `${window.location.origin}/qms/library?documentId=${encodeURIComponent(docId)}`;
    }
  }

  return null;
}

function isQmsLibraryRemoveAccessEmail(html, subject = "") {
  const blob = `${subject} ${html}`.toLowerCase();
  return (
    /document removed from your library/i.test(blob) ||
    /access to the following document has been removed/i.test(blob) ||
    /removed from the document library/i.test(blob)
  );
}

function isDocumentLibraryLink(href, linkText = "") {
  const hrefStr = String(href || "");
  const text = String(linkText || "").trim().toLowerCase();
  return (
    /qms\/library/i.test(hrefStr) ||
    /home\/index/i.test(hrefStr) ||
    /dms_documentlibrary/i.test(hrefStr) ||
    text === "open document"
  );
}

function sanitizeNotificationHtml(html, options = {}) {
  if (!html || typeof html !== "string") return "";
  const subject = options.subject || "";
  const stripDocLinks = isQmsLibraryRemoveAccessEmail(html, subject);
  const withoutBrokenFragments = html.replace(
    /Open document[""']\s*rel\s*=\s*[""'][^""']*[""'](?:\s*target\s*=\s*[""'][^""']*[""'])?\s*>\s*Open document/gi,
    "",
  );
  try {
    const doc = new DOMParser().parseFromString(withoutBrokenFragments, "text/html");
    doc.querySelectorAll("script, style, iframe, object, embed, meta, link").forEach((el) => el.remove());
    doc.querySelectorAll("*").forEach((el) => {
      [...el.attributes].forEach((attr) => {
        const n = attr.name.toLowerCase();
        const v = String(attr.value || "");
        if (n.startsWith("on")) el.removeAttribute(attr.name);
        if ((n === "href" || n === "src") && /^\s*javascript:/i.test(v)) el.removeAttribute(attr.name);
      });
    });
    doc.querySelectorAll("a").forEach((el) => {
      const href = String(el.getAttribute("href") || "").trim();
      const linkText = el.textContent || "";
      if (stripDocLinks && isDocumentLibraryLink(href, linkText)) {
        el.remove();
        return;
      }
      const reactUrl = toReactQmsLibraryUrl(href);
      if (reactUrl) {
        el.setAttribute("href", reactUrl);
        el.setAttribute("target", "_blank");
        el.setAttribute("rel", "noopener noreferrer");
        return;
      }
      if (!href || href === "#" || /^</.test(href)) {
        const text = linkText || "Open document";
        const span = doc.createElement("span");
        span.textContent = text;
        el.replaceWith(span);
      }
    });
    return doc.body.innerHTML;
  } catch {
    return "";
  }
}

function loadStarSet() {
  try {
    const raw = localStorage.getItem(STAR_STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

/** Browser-local calendar day key `YYYY-MM-DD` (for Today / Yesterday labels). */
function localTodayKey() {
  const n = new Date();
  return `${n.getFullYear()}-${pad2(n.getMonth() + 1)}-${pad2(n.getDate())}`;
}

function shiftLocalDayKey(dayKey, deltaDays) {
  const [y, m, d] = dayKey.split("-").map((x) => parseInt(x, 10));
  if (!y || !m || !d) return dayKey;
  const dt = new Date(y, m - 1, d, 12, 0, 0, 0);
  dt.setDate(dt.getDate() + deltaDays);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
}

/** Prefer same date source as the list (`ReceivedAtDisplay` → dd/MM/yyyy) so grouping matches the row. */
function calendarDayKeyFromRow(row) {
  const display = rowReceivedDisplay(row);
  if (display && display !== "—") {
    const m = display.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (m) {
      const day = parseInt(m[1], 10);
      const month = parseInt(m[2], 10);
      const year = parseInt(m[3], 10);
      if (
        year >= 1990 &&
        year <= 2100 &&
        month >= 1 &&
        month <= 12 &&
        day >= 1 &&
        day <= 31
      ) {
        return `${year}-${pad2(month)}-${pad2(day)}`;
      }
    }
  }
  const iso = rowReceived(row);
  const s = String(iso ?? "").trim();
  if (!s) return "";
  const m = s.match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{1,2}):(\d{2})(?::(\d{2}))?(\.\d+)?\s*(Z|[+-]\d{2}:\d{2}(?::\d{2})?)?\s*$/i,
  );
  if (m) {
    const year = parseInt(m[1], 10);
    const month = parseInt(m[2], 10);
    const day = parseInt(m[3], 10);
    const hasTz = !!(m[8] && String(m[8]).length);
    if (!hasTz) {
      return `${year}-${pad2(month)}-${pad2(day)}`;
    }
    const ms = new Date(s).getTime();
    if (Number.isNaN(ms)) return "";
    const d = new Date(ms);
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  }
  const ms = parseMailboxUtcMs(iso);
  if (Number.isNaN(ms)) return "";
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function labelForCalendarDayKey(dayKey, todayKey, yesterdayKey) {
  if (!dayKey) return "Unknown date";
  if (dayKey === todayKey) return "Today";
  if (dayKey === yesterdayKey) return "Yesterday";
  const [y, m, d] = dayKey.split("-").map((x) => parseInt(x, 10));
  return `${pad2(d)}/${pad2(m)}/${y}`;
}

function sortRowsByReceivedDesc(list) {
  return list.slice().sort((a, b) => {
    const ta = parseMailboxUtcMs(rowReceived(a));
    const tb = parseMailboxUtcMs(rowReceived(b));
    const na = Number.isNaN(ta) ? -Infinity : ta;
    const nb = Number.isNaN(tb) ? -Infinity : tb;
    return nb - na;
  });
}

/** One group per calendar day, newest days first; labels Today / Yesterday / dd/MM/yyyy. */
function groupByDate(rows) {
  const todayKey = localTodayKey();
  const yesterdayKey = shiftLocalDayKey(todayKey, -1);
  const bucket = new Map();
  const noDay = [];

  for (const r of rows) {
    const k = calendarDayKeyFromRow(r);
    if (!k) {
      noDay.push(r);
      continue;
    }
    if (!bucket.has(k)) bucket.set(k, []);
    bucket.get(k).push(r);
  }

  const keys = [...bucket.keys()].sort().reverse();
  const out = keys.map((k) => ({
    key: `day-${k}`,
    label: labelForCalendarDayKey(k, todayKey, yesterdayKey),
    items: sortRowsByReceivedDesc(bucket.get(k) || []),
  }));
  if (noDay.length) {
    out.push({
      key: "day-unknown",
      label: "Unknown date",
      items: sortRowsByReceivedDesc(noDay),
    });
  }
  return out;
}

function groupByModule(rows) {
  const map = new Map();
  for (const r of rows) {
    const bid = inboxModuleFilterIdForRow(r);
    const key = bid || `raw:${rowModule(r)}`;
    const meta = bid ? inboxModuleFilterBucketMeta(bid) : null;
    const raw = rowModule(r);
    const label =
      meta?.label ||
      (!raw || raw.toLowerCase() === "general"
        ? "General"
        : raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()));
    if (!map.has(key)) map.set(key, { label, items: [] });
    map.get(key).items.push(r);
  }
  return [...map.entries()]
    .sort((a, b) => a[1].label.localeCompare(b[1].label, undefined, { sensitivity: "base" }))
    .map(([key, { label, items }]) => ({
      key: `m-${key}`,
      label,
      items: sortRowsByReceivedDesc(items),
    }));
}

/**
 * Staff inbox — Gmail-like split view: label rail, dense message list, reading pane.
 */
const NotificationCenterInbox = () => {
  const replyAttachedHeadingId = useId();
  const location = useLocation();
  const { userData } = useUserData();

  const organizationRefId = useMemo(
    () => resolveEmailPlatformOrganizationGuid(userData),
    [userData],
  );

  const organizationDisplayName = useMemo(() => sessionOrganizationDisplayName(userData), [userData]);

  const staffReadBy = useMemo(
    () => String(userData?.StaffId || "").trim(),
    [userData?.StaffId],
  );

  const [take] = useState(200);
  const [receivedRows, setReceivedRows] = useState([]);
  const [sentRows, setSentRows] = useState([]);
  const [loadingInbox, setLoadingInbox] = useState(false);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const detailKindRef = useRef("Inbound");

  const [search, setSearch] = useState("");
  const [scope, setScope] = useState("all");
  const [moduleFilter, setModuleFilter] = useState("");
  const [groupBy, setGroupBy] = useState("date");
  const [starredIds, setStarredIds] = useState(() => loadStarSet());

  /** Reply composer: To is fixed (original sender); subject editable; body = rich text only. */
  const [replyComposerOpen, setReplyComposerOpen] = useState(false);
  const [replyRecipientEmail, setReplyRecipientEmail] = useState("");
  const [composerSubject, setComposerSubject] = useState("");
  const [composerBodyHtml, setComposerBodyHtml] = useState(EMPTY_QUILL_HTML);
  const [replyEditorKey, setReplyEditorKey] = useState(0);
  const [originalThreadExpanded, setOriginalThreadExpanded] = useState(false);
  const [replyAttachmentFiles, setReplyAttachmentFiles] = useState([]);
  const [replyAttachDragActive, setReplyAttachDragActive] = useState(false);
  const replyFileInputRef = useRef(null);
  const [composerSending, setComposerSending] = useState(false);
  const [composerError, setComposerError] = useState("");
  const [sendSuccess, setSendSuccess] = useState("");

  const isSystemAdmin = isSystemAdministratorRole(getSessionRoleName(userData));
  const canAccessSystemSent = canViewSystemSent(isSystemAdmin);
  const hideModuleFilter = isSponsorCroSession(userData);

  const hasUserManagement = useMemo(() => {
    const keys = isSystemAdmin
      ? getAllPermissionKeys()
      : parsePermissions(userData?.Permission);
    return hasPermission(keys, "USER_MANAGEMENT");
  }, [isSystemAdmin, userData?.Permission]);

  const canMonitorOrgInbound = canViewOrgInboundReplies({
    isSystemAdmin,
    hasUserManagement,
  });

  const staffEmail = useMemo(
    () => String(userData?.EmailId || userData?.emailId || "").trim(),
    [userData?.EmailId, userData?.emailId],
  );

  const inboxRows = useMemo(
    () =>
      buildInboxRows(receivedRows, staffEmail, {
        keepOrgInboundReplies: canMonitorOrgInbound,
      }),
    [receivedRows, staffEmail, canMonitorOrgInbound],
  );

  const allKnownRows = useMemo(
    () => mergeMailboxRows(receivedRows, sentRows),
    [receivedRows, sentRows],
  );

  const loadInbox = useCallback(async () => {
    if (!organizationRefId) {
      setReceivedRows([]);
      setSentRows([]);
      setError("");
      return;
    }
    setLoadingInbox(true);
    setError("");
    try {
      const inboundPromise = listInboundByStaffRecipient({
        organizationRefId,
        take,
      });
      const orgInboundPromise = canMonitorOrgInbound
        ? listInboundByOrganization(organizationRefId, take)
        : Promise.resolve(null);
      // System Sent (System Administrator only): org noreply / platform outbound.
      // Prefer dedicated org list; fall back to ListSentByStaff with noreply flag.
      const sentPromise = canAccessSystemSent
        ? listOutboundNoreplyByOrganization({ organizationRefId, take }).then(
            async (noreplyRes) => {
              if (apiOk(noreplyRes) && Array.isArray(noreplyRes.data)) {
                return noreplyRes;
              }
              return listSentByStaff({
                organizationRefId,
                take,
                includeOrgNoreplyOutbound: true,
              });
            },
          )
        : Promise.resolve(null);

      const [res, orgRes, sentRes] = await Promise.all([
        inboundPromise,
        orgInboundPromise,
        sentPromise,
      ]);
      if (!apiOk(res)) {
        setReceivedRows([]);
        setSentRows([]);
        setError(res?.message || "Could not load notifications.");
        return;
      }
      const staffList = Array.isArray(res.data) ? res.data : [];
      const orgList =
        canMonitorOrgInbound && apiOk(orgRes) && Array.isArray(orgRes.data)
          ? orgRes.data
          : [];
      setReceivedRows(
        canMonitorOrgInbound
          ? mergeStaffAndOrgInboundRows(staffList, orgList)
          : staffList,
      );
      setSentRows(
        canAccessSystemSent && apiOk(sentRes) && Array.isArray(sentRes.data)
          ? sentRes.data
          : [],
      );
    } catch (e) {
      setReceivedRows([]);
      setSentRows([]);
      setError(e?.message || "Failed to load inbox.");
    } finally {
      setLoadingInbox(false);
    }
  }, [organizationRefId, take, canAccessSystemSent, canMonitorOrgInbound]);

  useEffect(() => {
    loadInbox();
  }, [loadInbox]);

  useEffect(() => {
    setReplyComposerOpen(false);
    setReplyRecipientEmail("");
    setComposerSubject("");
    setComposerBodyHtml(EMPTY_QUILL_HTML);
    setReplyEditorKey((k) => k + 1);
    setOriginalThreadExpanded(false);
    setReplyAttachmentFiles([]);
    setReplyAttachDragActive(false);
    setComposerError("");
    setSendSuccess("");
  }, [selectedId]);

  const counts = useMemo(() => {
    const all = inboxRows.length;
    const unread = inboxRows.filter((r) => !readIsReadFlag(r)).length;
    const sent = sentRows.length;
    const starred = allKnownRows.filter((r) => starredIds.has(rowId(r))).length;
    return { all, unread, sent, starred };
  }, [inboxRows, sentRows, allKnownRows, starredIds]);

  const inboxModuleFilterList = useMemo(
    () => visibleInboxModuleBuckets(userData),
    [userData],
  );

  const inboxModuleBucketCounts = useMemo(() => {
    const counts = new Map();
    for (const b of INBOX_MODULE_FILTER_BUCKETS) counts.set(b.id, 0);
    for (const r of inboxRows) {
      const id = inboxModuleFilterIdForRow(r);
      if (id && counts.has(id)) counts.set(id, counts.get(id) + 1);
    }
    return counts;
  }, [inboxRows]);

  useEffect(() => {
    if (hideModuleFilter) {
      setModuleFilter("");
      if (groupBy === "module") setGroupBy("date");
    }
  }, [hideModuleFilter, groupBy]);

  useEffect(() => {
    if (!moduleFilter) return;
    if (!inboxModuleFilterList.some((b) => b.id === moduleFilter)) setModuleFilter("");
  }, [moduleFilter, inboxModuleFilterList]);

  useEffect(() => {
    if (scope === "sent" && !canAccessSystemSent) setScope("all");
  }, [scope, canAccessSystemSent]);

  const filteredRows = useMemo(() => {
    let list;
    if (scope === "sent") list = sentRows.slice();
    else if (scope === "starred")
      list = allKnownRows.filter((r) => starredIds.has(rowId(r)));
    else if (scope === "unread") list = inboxRows.filter((r) => !readIsReadFlag(r));
    else list = inboxRows.slice();

    if (moduleFilter) {
      list = list.filter((r) => rowMatchesInboxModuleFilter(r, moduleFilter));
    }

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((r) => {
        const blob = [
          rowSubject(r),
          rowFrom(r),
          rowPreview(r),
          rowModule(r),
          String(r.RecordId || r.recordId || ""),
        ]
          .join(" ")
          .toLowerCase();
        return blob.includes(q);
      });
    }
    return list;
  }, [scope, sentRows, allKnownRows, inboxRows, moduleFilter, search, starredIds]);

  const groupedList = useMemo(() => {
    if (groupBy === "module") return groupByModule(filteredRows);
    return groupByDate(filteredRows);
  }, [filteredRows, groupBy]);

  useEffect(() => {
    if (!selectedId) return;
    const still = filteredRows.some((r) => rowId(r) === selectedId);
    if (!still) {
      setSelectedId("");
      setDetail(null);
    }
  }, [filteredRows, selectedId]);

  const openDetail = useCallback(
    async (id, mailboxKind = "Inbound") => {
      if (!id || !organizationRefId) return;
      const kind = String(mailboxKind || "Inbound");
      detailKindRef.current = kind;
      setSelectedId(id);
      setLoadingDetail(true);
      setError("");
      try {
        const listRow = allKnownRows.find((r) => rowId(r) === id);
        const useSentDetail = rowIsInSentList(listRow, sentRows);
        const isOutbound = String(kind).toLowerCase() === "outbound";

        let res;
        if (useSentDetail && isOutbound && canAccessSystemSent) {
          res = await getOutboundDetailForOrgAdmin(organizationRefId, id);
          if (!apiOk(res)) {
            res = await getSentDetail({
              organizationRefId,
              emailOutboundMessageId: id,
              mailboxRowKind: "Outbound",
            });
          }
        } else if (useSentDetail) {
          res = await getSentDetail({
            organizationRefId,
            emailInboundMessageId: id,
            emailOutboundMessageId: id,
            mailboxRowKind: kind,
          });
        } else if (isOutbound) {
          res = await getOutboundDetailForStaff(organizationRefId, id);
        } else {
          res = await getInboundDetail(organizationRefId, id);
        }
        if (!apiOk(res)) {
          setDetail(null);
          setError(res?.message || "Could not load message detail.");
          return;
        }
        setDetail(res.data || null);

        if (listRow && !readIsReadFlag(listRow)) {
          try {
            const readRes = await markInboundRead(organizationRefId, id, {
              isRead: true,
              readByStaffId: staffReadBy,
              mailboxRowKind: kind,
            });
            if (apiOk(readRes)) {
              await loadInbox();
            }
          } catch {
            /* non-fatal */
          }
        }
      } catch (e) {
        setDetail(null);
        setError(e?.message || "Detail request failed.");
      } finally {
        setLoadingDetail(false);
      }
    },
    [
      organizationRefId,
      allKnownRows,
      sentRows,
      staffReadBy,
      loadInbox,
      canAccessSystemSent,
    ],
  );

  const pendingSelectId = location.state?.selectedMessageId;

  useEffect(() => {
    if (!pendingSelectId || allKnownRows.length === 0) return;
    const match = allKnownRows.find((r) => rowId(r) === String(pendingSelectId));
    if (match) {
      openDetail(String(pendingSelectId), rowMailboxKind(match));
    }
  }, [pendingSelectId, allKnownRows, openDetail]);

  const handleMarkRead = useCallback(
    async (id, isRead, mailboxKind = "Inbound") => {
      if (!id || !organizationRefId) return;
      setError("");
      try {
        const res = await markInboundRead(organizationRefId, id, {
          isRead,
          readByStaffId: staffReadBy,
          mailboxRowKind: mailboxKind,
        });
        if (!apiOk(res)) {
          setError(res?.message || "Could not update read state.");
          return;
        }
        await loadInbox();
      } catch (e) {
        setError(e?.message || "Mark read failed.");
      }
    },
    [organizationRefId, staffReadBy, loadInbox],
  );

  const handleMarkAllRead = useCallback(async () => {
    const unreadRows = inboxRows.filter((r) => !readIsReadFlag(r));
    if (unreadRows.length === 0) return;
    setError("");
    try {
      await Promise.all(
        unreadRows.map((row) =>
          markInboundRead(organizationRefId, rowId(row), {
            isRead: true,
            readByStaffId: staffReadBy,
            mailboxRowKind: rowMailboxKind(row),
          }),
        ),
      );
      await loadInbox();
    } catch (e) {
      setError(e?.message || "Could not mark all as read.");
    }
  }, [inboxRows, organizationRefId, staffReadBy, loadInbox]);

  const toggleStar = useCallback((e, id) => {
    e.stopPropagation();
    if (!id) return;
    setStarredIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        localStorage.setItem(STAR_STORAGE_KEY, JSON.stringify([...next]));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const selectedRow = useMemo(
    () => filteredRows.find((r) => rowId(r) === selectedId) || null,
    [filteredRows, selectedId],
  );

  const headerFields = detail?.Header || detail?.header || null;
  const bodyFields = detail?.Body || detail?.body || null;
  const attachments = detail?.Attachments || detail?.attachments || [];

  const detailSubjectDisplay = useMemo(() => {
    const raw = String(
      headerFields?.Subject ??
        headerFields?.subject ??
        (selectedRow ? rowSubject(selectedRow) : "") ??
        "",
    ).trim();
    return stripBracketedOrgSubject(raw, organizationDisplayName);
  }, [headerFields, selectedRow, organizationDisplayName]);

  const detailRecipientLabel = useMemo(() => {
    const isSentView =
      scope === "sent" || rowIsSentMailboxRow(selectedRow, sentRows);
    if (isSentView) {
      return resolveSentRecipientDisplay(selectedRow, headerFields);
    }
    return "";
  }, [headerFields, scope, selectedRow, sentRows]);

  const detailPlainBody = useMemo(() => {
    if (!bodyFields) return "";
    const t = bodyFields.TextBody || bodyFields.textBody;
    let raw;
    if (t) raw = String(t);
    else {
      const h = bodyFields.HtmlBody || bodyFields.htmlBody;
      raw = htmlToPlainText(h || "");
    }
    return stripLeadingOrgFromPlainText(raw, organizationDisplayName);
  }, [bodyFields, organizationDisplayName]);

  const detailHtmlContent = useMemo(() => {
    if (!bodyFields) return "";
    const h = bodyFields.HtmlBody ?? bodyFields.htmlBody;
    if (!h || typeof h !== "string" || !String(h).trim()) return "";
    const stripped = stripLeadingOrgFromHtml(String(h), organizationDisplayName);
    return sanitizeNotificationHtml(stripped, { subject: detailSubjectDisplay });
  }, [bodyFields, organizationDisplayName, detailSubjectDisplay]);

  const closeComposer = useCallback(() => {
    setReplyComposerOpen(false);
    setReplyRecipientEmail("");
    setComposerSubject("");
    setComposerBodyHtml(EMPTY_QUILL_HTML);
    setReplyEditorKey((k) => k + 1);
    setOriginalThreadExpanded(false);
    setReplyAttachmentFiles([]);
    setReplyAttachDragActive(false);
    setComposerError("");
  }, []);

  const openReplyComposer = useCallback(() => {
    if (!headerFields) return;
    setComposerError("");
    const fromDisp = formatCell(headerFields.FromAddress ?? headerFields.fromAddress);
    const to = parseEmailFromHeader(fromDisp);
    if (!to) {
      setError("Could not detect sender email for reply.");
      return;
    }
    setError("");
    setReplyRecipientEmail(to);
    setComposerSubject(withRePrefix(detailSubjectDisplay));
    setComposerBodyHtml(EMPTY_QUILL_HTML);
    setReplyEditorKey((k) => k + 1);
    setOriginalThreadExpanded(false);
    setReplyAttachmentFiles([]);
    setReplyAttachDragActive(false);
    setReplyComposerOpen(true);
  }, [headerFields, detailSubjectDisplay, detail]);

  const handleSendComposer = useCallback(async () => {
    setComposerError("");
    const subj = composerSubject.trim();
    const html = composerBodyHtml || "";
    if (!organizationRefId) {
      setComposerError("Organisation context is required.");
      return;
    }
    const to = replyRecipientEmail.trim();
    if (!to) {
      setComposerError("Reply address is missing. Close and pick another message.");
      return;
    }
    const displayName =
      organizationDisplayName.trim() ||
      String(userData?.OrganizationName || userData?.organizationName || "").trim();
    if (!displayName) {
      setComposerError("Organization display name is missing; cannot send.");
      return;
    }
    if (isQuillBodyEmpty(html)) {
      setComposerError("Write a message before sending.");
      return;
    }
    const plain = htmlToPlainText(html);
    const maxFile = 4 * 1024 * 1024;
    const maxTotal = 9 * 1024 * 1024;
    let total = 0;
    for (const f of replyAttachmentFiles) {
      if (f.size > maxFile) {
        setComposerError(`Attachment "${f.name}" is too large (max 4 MB each).`);
        return;
      }
      total += f.size;
    }
    if (total > maxTotal) {
      setComposerError("Attachments exceed total size limit (9 MB).");
      return;
    }
    setComposerSending(true);
    try {
      const attachmentParts = [];
      for (const f of replyAttachmentFiles) {
        attachmentParts.push(await readFileAsAttachmentPart(f));
      }
      const res = await sendOutboundTransactional({
        organizationRefId,
        module: rowModule(selectedRow || {}),
        recordId: recordIdFromRow(selectedRow || {}),
        displayFromName: displayName,
        toAddress: to,
        subject: subj,
        bodyText: plain,
        bodyHtml: html,
        attachments: attachmentParts,
      });
      if (!apiOk(res)) {
        setComposerError(res?.message || "Send failed.");
        return;
      }
      setSendSuccess(String(res?.message || "Message sent.").trim() || "Message sent.");
      closeComposer();
      await loadInbox();
    } catch (e) {
      setComposerError(e?.message || "Send failed.");
    } finally {
      setComposerSending(false);
    }
  }, [
    organizationRefId,
    organizationDisplayName,
    userData,
    replyRecipientEmail,
    composerSubject,
    composerBodyHtml,
    replyAttachmentFiles,
    selectedRow,
    loadInbox,
    closeComposer,
  ]);

  const showFullOriginal = !replyComposerOpen || originalThreadExpanded;

  const mergeReplyFilesFromList = useCallback((list) => {
    if (!list || !list.length) return;
    // Snapshot immediately: clearing the file input empties the live FileList before
    // React may run the setState updater, so iterating `list` later would add nothing.
    const files = Array.from(list);
    if (!files.length) return;
    const maxFiles = 10;
    const fileKey = (f) => `${f.name}\0${f.size}\0${f.lastModified}`;
    setReplyAttachmentFiles((prev) => {
      const next = prev.slice();
      const seen = new Set(next.map(fileKey));
      for (let i = 0; i < files.length && next.length < maxFiles; i += 1) {
        const f = files[i];
        if (!f) continue;
        const k = fileKey(f);
        if (seen.has(k)) continue;
        seen.add(k);
        next.push(f);
      }
      return next;
    });
  }, []);

  const onReplyFilesPicked = useCallback(
    (e) => {
      mergeReplyFilesFromList(e.target?.files);
      e.target.value = "";
    },
    [mergeReplyFilesFromList],
  );

  const onComposeDragEnter = useCallback((e) => {
    if (!dataTransferHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    setReplyAttachDragActive(true);
  }, []);

  const onComposeDragLeave = useCallback((e) => {
    const next = e.relatedTarget;
    if (next && e.currentTarget.contains(next)) return;
    setReplyAttachDragActive(false);
  }, []);

  const onComposeDragOver = useCallback((e) => {
    if (!dataTransferHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    try {
      e.dataTransfer.dropEffect = "copy";
    } catch {
      /* ignore */
    }
  }, []);

  const onComposeDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setReplyAttachDragActive(false);
      const dt = e.dataTransfer;
      if (dt?.files?.length) mergeReplyFilesFromList(dt.files);
    },
    [mergeReplyFilesFromList],
  );

  const removeReplyAttachment = useCallback((index) => {
    setReplyAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  return (
    <div className="notification-center-inbox">
      {error ? <div className="nc-alert">{error}</div> : null}

      <div className="nc-app">
        <aside className="nc-sidebar" aria-label="Inbox filters">
          <div>
            <div className="nc-sidebar__title">Quick filters</div>
            <nav className="nc-nav">
              {[
                ["all", "Inbox", counts.all],
                ["unread", "Unread", counts.unread],
                ...(canAccessSystemSent
                  ? [["sent", "System Sent", counts.sent]]
                  : []),
                ["starred", "Starred", counts.starred],
              ].map(([key, label, n]) => (
                <button
                  key={key}
                  type="button"
                  className={`nc-nav__btn ${scope === key ? "nc-nav__btn--active" : ""}`}
                  onClick={() => setScope(key)}
                >
                  <span>{label}</span>
                  <span className="nc-nav__count">{n}</span>
                </button>
              ))}
            </nav>
          </div>

          {hideModuleFilter ? null : (
          <div>
            <div className="nc-sidebar__title">Module</div>
            <div className="nc-modules">
              <button
                type="button"
                className={`nc-module-chip ${!moduleFilter ? "nc-module-chip--active" : ""}`}
                onClick={() => setModuleFilter("")}
              >
                <span className="d-flex align-items-center">
                  <span className="nc-module-dot" style={{ background: "#71717a" }} />
                  All modules
                </span>
                <span className="nc-nav__count">{inboxRows.length}</span>
              </button>
              {inboxModuleFilterList.map((bucket) => {
                const n = inboxModuleBucketCounts.get(bucket.id) ?? 0;
                return (
                  <button
                    key={bucket.id}
                    type="button"
                    className={`nc-module-chip ${
                      moduleFilter === bucket.id ? "nc-module-chip--active" : ""
                    }`}
                    onClick={() => setModuleFilter(moduleFilter === bucket.id ? "" : bucket.id)}
                  >
                    <span className="d-flex align-items-center text-truncate" style={{ maxWidth: 160 }}>
                      <span
                        className="nc-module-dot"
                        style={{ background: getSidebarModuleDotColor(bucket.id) }}
                      />
                      <span className="text-truncate" title={bucket.label}>
                        {bucket.label}
                      </span>
                    </span>
                    <span className="nc-nav__count">{n}</span>
                  </button>
                );
              })}
            </div>
          </div>
          )}

        </aside>

        <main className="nc-list" aria-label="Message list">
          <div className="nc-list__head nc-list__head--gmail">
            <div className="nc-list__head-main">
              <i className="fa fa-search nc-list__search-icon" aria-hidden="true" />
              <input
                type="search"
                className="nc-gmail-search"
                placeholder="Search mail"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoComplete="off"
                aria-label="Search mail"
              />
            </div>
            <div className="nc-list__head-actions">
              {hideModuleFilter ? null : (
              <div className="nc-seg" role="group" aria-label="Group by">
                <button
                  type="button"
                  className={`nc-seg__btn ${groupBy === "date" ? "nc-seg__btn--active" : ""}`}
                  onClick={() => setGroupBy("date")}
                >
                  By date
                </button>
                <button
                  type="button"
                  className={`nc-seg__btn ${groupBy === "module" ? "nc-seg__btn--active" : ""}`}
                  onClick={() => setGroupBy("module")}
                >
                  By module
                </button>
              </div>
              )}
              {counts.unread > 0 ? (
                <button
                  type="button"
                  className="nc-btn nc-btn--sm"
                  title="Mark all as read"
                  disabled={loadingInbox || !organizationRefId}
                  onClick={() => handleMarkAllRead()}
                >
                  Mark all read
                </button>
              ) : null}
              <button
                type="button"
                className="nc-btn nc-btn--icon"
                title="Refresh"
                disabled={loadingInbox || !organizationRefId}
                onClick={() => loadInbox()}
              >
                <i className="fa fa-rotate-right" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="nc-list__scroll">
            {!organizationRefId ? (
              <div className="nc-empty">Select an organisation context to load your inbox.</div>
            ) : filteredRows.length === 0 ? (
              <div className="nc-empty">
                {loadingInbox
                  ? "Loading messages…"
                  : "No messages match your filters. Try another view or clear search."}
              </div>
            ) : (
              groupedList.map((group) => (
                <React.Fragment key={group.key}>
                  <div
                    className={`nc-group-label ${
                      String(group.key).startsWith("day-") ? "nc-group-label--date" : ""
                    }`}
                  >
                    {group.label}
                  </div>
                  {group.items.map((row) => {
                    const id = rowId(row);
                    const subjectRow = stripBracketedOrgSubject(rowSubject(row), organizationDisplayName);
                    const from = rowFrom(row);
                    const fromShort = shortFrom(from);
                    const senderName = senderDisplayName(from);
                    const prevRaw = rowPreview(row);
                    const prevDisplay = previewLooksLikeHtml(prevRaw)
                      ? sanitizeNotificationHtml(
                          stripLeadingOrgFromHtml(String(prevRaw), organizationDisplayName),
                          { subject: subjectRow },
                        )
                      : stripLeadingOrgFromPlainText(String(prevRaw), organizationDisplayName);
                    const recv = rowReceived(row);
                    const mailboxKind = rowMailboxKind(row);
                    const unread = !readIsReadFlag(row);
                    const starred = starredIds.has(id);
                    const modName = rowModule(row);
                    const modLabel = rowModuleBadgeLabel(row);
                    const modStyle = getSidebarModuleBadgeStyle(modName);
                    return (
                      <div
                        key={id || subjectRow + from}
                        className={`nc-gmail-row ${selectedId === id ? "nc-gmail-row--selected" : ""} ${
                          unread ? "nc-gmail-row--unread" : "nc-gmail-row--read"
                        } nc-gmail-row--has-quick`}
                        role="button"
                        tabIndex={0}
                        aria-selected={selectedId === id}
                        aria-label={`${unread ? "Unread. " : ""}${senderName}: ${subjectRow}`}
                        onClick={() => openDetail(id, mailboxKind)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openDetail(id, mailboxKind);
                          }
                        }}
                      >
                        <button
                          type="button"
                          className={`nc-gmail-row__star ${starred ? "nc-gmail-row__star--on" : ""}`}
                          title={starred ? "Starred" : "Not starred"}
                          aria-label={starred ? "Remove star" : "Star"}
                          onClick={(e) => toggleStar(e, id)}
                        >
                          <i
                            className={starred ? "fas fa-star" : "far fa-star"}
                            aria-hidden="true"
                          />
                        </button>
                        <span
                          className={`nc-gmail-row__avatar-wrap${unread ? " nc-gmail-row__avatar-wrap--unread" : ""}`}
                        >
                          <span
                            className="capa-list-owner-avatar nc-gmail-row__avatar"
                            style={{
                              backgroundColor: getAvatarColor(fromShort || from || "?"),
                            }}
                            title={from || fromShort}
                            aria-label={`Sender: ${from || fromShort}`}
                          >
                            {getInitials(fromShort || from || "?")}
                          </span>
                        </span>
                        <span className="nc-gmail-row__sender" title={from || senderName}>
                          {senderName}
                        </span>
                        <span className="nc-gmail-row__snippet-block">
                          <span
                            className="nc-gmail-row__tag nc-gmail-row__tag--module"
                            style={modStyle}
                            title={modName}
                          >
                            {modLabel}
                          </span>
                          <span className="nc-gmail-row__subject">{subjectRow}</span>
                          {prevDisplay ? (
                            <>
                              <span className="nc-gmail-row__dash"> — </span>
                              {previewLooksLikeHtml(prevRaw) ? (
                                <span
                                  className="nc-gmail-row__snippet nc-gmail-row__snippet--html"
                                  // eslint-disable-next-line react/no-danger
                                  dangerouslySetInnerHTML={{
                                    __html: prevDisplay,
                                  }}
                                />
                              ) : (
                                <span className="nc-gmail-row__snippet">{prevDisplay}</span>
                              )}
                            </>
                          ) : null}
                        </span>
                        <span className="nc-gmail-row__date" title={recv ? String(recv) : ""}>
                          {selectedId === id ? (
                            <span className="nc-gmail-row__open-mark">Open</span>
                          ) : null}
                          {rowReceivedDisplay(row)}
                        </span>
                        {unread ? (
                          <div className="nc-gmail-row__quick">
                            <button
                              type="button"
                              className="nc-gmail-row__quick-btn"
                              title="Mark as read"
                              aria-label="Mark as read"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkRead(id, true, mailboxKind);
                              }}
                            >
                              <i className="fa fa-check" aria-hidden="true" />
                            </button>
                          </div>
                        ) : (
                          <div className="nc-gmail-row__quick nc-gmail-row__quick--read">
                            <button
                              type="button"
                              className="nc-gmail-row__quick-btn"
                              title="Mark as unread"
                              aria-label="Mark as unread"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkRead(id, false, mailboxKind);
                              }}
                            >
                              <i className="fa fa-envelope" aria-hidden="true" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))
            )}
          </div>
        </main>

        <section className="nc-detail" aria-label="Message detail">
          <div className="nc-detail__scroll">
            {sendSuccess ? (
              <div className="nc-banner nc-banner--success" role="status">
                <span>{sendSuccess}</span>
                <button
                  type="button"
                  className="nc-banner__dismiss"
                  onClick={() => setSendSuccess("")}
                  aria-label="Dismiss notification"
                >
                  ×
                </button>
              </div>
            ) : null}
            {!selectedId ? (
              <div className="nc-empty nc-empty--read">
                <div className="nc-empty__icon" aria-hidden="true">
                  <i className="fa fa-envelope-open" />
                </div>
                <p className="nc-empty__title">No message selected</p>
                <p className="nc-empty__hint">Pick an item from the list to read it here.</p>
              </div>
            ) : loadingDetail ? (
              <div className="nc-empty nc-empty--read">Loading…</div>
            ) : !headerFields ? (
              <div className="nc-empty nc-empty--read">Could not load this message.</div>
            ) : (
              <article className="nc-gmail-read">
                {replyComposerOpen ? (
                  <button
                    type="button"
                    className="nc-thread-summary-toggle"
                    onClick={() => setOriginalThreadExpanded((v) => !v)}
                    aria-expanded={originalThreadExpanded}
                  >
                    <i
                      className={`fa fa-fw ${originalThreadExpanded ? "fa-chevron-down" : "fa-chevron-right"}`}
                      aria-hidden="true"
                    />
                    <span className="nc-thread-summary-toggle__main">
                      {formatCell(headerFields.FromAddress ?? headerFields.fromAddress)} ·{" "}
                      {detailSubjectDisplay} · {rowReceivedDisplay(selectedRow || {})}
                    </span>
                    <span className="nc-thread-summary-toggle__hint">
                      {originalThreadExpanded ? "Hide original" : "Show original message"}
                    </span>
                  </button>
                ) : null}

                {showFullOriginal ? (
                  <>
                    <h1 className="nc-gmail-read__subject">{detailSubjectDisplay}</h1>

                    <div className="nc-gmail-read__meta-bar">
                      <div className="nc-gmail-read__who">
                        <span className="nc-gmail-read__avatar" aria-hidden="true">
                          {shortFrom(
                            formatCell(headerFields.FromAddress ?? headerFields.fromAddress),
                          )
                            .slice(0, 1)
                            .toUpperCase()}
                        </span>
                        <div>
                          <div className="nc-gmail-read__from-line">
                            <strong>
                              {formatCell(headerFields.FromAddress ?? headerFields.fromAddress)}
                            </strong>
                            {detailRecipientLabel ? (
                              <span className="nc-gmail-read__to-me">
                                {" "}
                                to {detailRecipientLabel}
                              </span>
                            ) : (
                              <span className="nc-gmail-read__to-me"> to me</span>
                            )}
                          </div>
                          <div className="nc-gmail-read__sub-meta">
                            <span
                              className="nc-gmail-read__module-badge"
                              style={getSidebarModuleBadgeStyle(rowModule(selectedRow || {}))}
                              title={rowModule(selectedRow || {})}
                            >
                              {rowModuleBadgeLabel(selectedRow || {}, 28)}
                            </span>
                            <span className="nc-gmail-read__sub-meta-time">
                              {rowReceivedDisplay(selectedRow || {})}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="nc-gmail-read__meta-actions">
                        {readIsReadFlag(selectedRow) ? (
                          <button
                            type="button"
                            className="nc-gmail-read__icon-btn"
                            onClick={() =>
                              handleMarkRead(selectedId, false, rowMailboxKind(selectedRow))
                            }
                            title="Mark as unread"
                            aria-label="Mark as unread"
                          >
                            <i className="fa fa-envelope" aria-hidden="true" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="nc-gmail-read__icon-btn"
                            onClick={() =>
                              handleMarkRead(selectedId, true, rowMailboxKind(selectedRow))
                            }
                            title="Mark as read"
                            aria-label="Mark as read"
                          >
                            <i className="fa fa-envelope-open" aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    </div>

                    {detailHtmlContent ? (
                      <div
                        className="nc-gmail-read__body nc-gmail-read__body--html"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: detailHtmlContent }}
                      />
                    ) : (
                      <div className="nc-gmail-read__body nc-gmail-read__body--plain">
                        {detailPlainBody || "No message body."}
                      </div>
                    )}

                    {Array.isArray(attachments) && attachments.length > 0 ? (
                      <div className="nc-gmail-read__attach">
                        <div className="nc-gmail-read__attach-title">
                          {attachments.length} attachment{attachments.length === 1 ? "" : "s"}
                        </div>
                        {attachments.map((a, idx) => (
                          <div key={idx} className="nc-gmail-read__attach-row">
                            <i className="fa fa-file-alt" aria-hidden="true" />
                            <span>
                              {(a.FileName || a.fileName || "file") +
                                " · " +
                                (a.ContentType || a.contentType || "unknown")}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </>
                ) : null}

                {replyComposerOpen ? (
                  <div
                    className={`nc-reply-compose${replyAttachDragActive ? " nc-reply-compose--drag-files" : ""}`}
                    aria-label="Reply"
                    onDragEnter={onComposeDragEnter}
                    onDragLeave={onComposeDragLeave}
                    onDragOver={onComposeDragOver}
                    onDrop={onComposeDrop}
                  >
                    {composerError ? <div className="nc-reply-compose__err">{composerError}</div> : null}
                    <input
                      ref={replyFileInputRef}
                      type="file"
                      className="nc-reply-compose__file-input-hidden visually-hidden"
                      multiple
                      onChange={onReplyFilesPicked}
                      tabIndex={-1}
                    />
                    <label className="nc-reply-compose__field">
                      <span className="nc-reply-compose__field-label">Subject</span>
                      <input
                        type="text"
                        className="nc-reply-compose__input"
                        value={composerSubject}
                        onChange={(e) => setComposerSubject(e.target.value)}
                      />
                    </label>
                    <div className="nc-reply-compose__field nc-reply-compose__field--grow">
                      <span className="nc-reply-compose__field-label">Message</span>
                      <TextEditor
                        key={replyEditorKey}
                        value={composerBodyHtml}
                        onChange={setComposerBodyHtml}
                        placeholder="Write your reply…"
                        height="12rem"
                        minHeight="10rem"
                        showImageButton={false}
                        blockImages
                      />
                    </div>

                    {replyAttachmentFiles.length > 0 ? (
                      <div className="nc-reply-compose__attached-block">
                        <div className="nc-reply-compose__attached-heading" id={replyAttachedHeadingId}>
                          Attached files ({replyAttachmentFiles.length})
                        </div>
                        <ul
                          className="nc-reply-compose__file-list"
                          aria-labelledby={replyAttachedHeadingId}
                        >
                        {replyAttachmentFiles.map((f, idx) => (
                          <li key={`${f.name}-${f.size}-${idx}`} className="nc-reply-compose__file-chip">
                            <span className="nc-reply-compose__file-chip-icon" aria-hidden="true">
                              <i className="fa fa-file-alt" />
                            </span>
                            <span className="nc-reply-compose__file-chip-main">
                              <span className="nc-reply-compose__file-chip-name text-truncate" title={f.name}>
                                {f.name}
                              </span>
                              <span className="nc-reply-compose__file-chip-meta">
                                {f.size < 1024 ? `${f.size} B` : `${Math.round(f.size / 1024)} KB`}
                              </span>
                            </span>
                            <button
                              type="button"
                              className="nc-reply-compose__file-remove"
                              onClick={() => removeReplyAttachment(idx)}
                              aria-label={`Remove ${f.name}`}
                            >
                              <i className="fa fa-times" aria-hidden="true" />
                            </button>
                          </li>
                        ))}
                      </ul>
                      </div>
                    ) : null}

                    <div className="nc-reply-compose__footer">
                      <div className="nc-reply-compose__footer-start">
                        <button
                          type="button"
                          className="nc-btn nc-btn--sm nc-btn--primary nc-reply-compose__send-btn"
                          disabled={composerSending}
                          onClick={handleSendComposer}
                        >
                          {composerSending ? "Sending…" : "Send"}
                        </button>
                        <button
                          type="button"
                          className="nc-reply-compose__footer-icon-btn"
                          disabled={composerSending}
                          onClick={() => replyFileInputRef.current?.click()}
                          title="Attach files (up to 10, 4 MB each, 9 MB total)"
                          aria-label="Attach files"
                        >
                          <i className="fa fa-paperclip" aria-hidden="true" />
                        </button>
                      </div>
                      <div className="nc-reply-compose__footer-end">
                        <button
                          type="button"
                          className="nc-reply-compose__footer-icon-btn nc-reply-compose__footer-icon-btn--danger-hover"
                          disabled={composerSending}
                          onClick={closeComposer}
                          title="Discard"
                          aria-label="Discard"
                        >
                          <i className="fa fa-trash" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </article>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default NotificationCenterInbox;
